import { cleanHtml, GetBackgroundStyle } from "@talladega/helpers";
import { WarrantyVehicleComponentLayout } from "@modules/cms/types/WarrantyVehicleComponentLayout";
import {
  SedanEn,
  SedanFr,
  SuvEn,
  SuvFr,
  TruckEn,
  TruckFr,
} from "@public/images/purchase/coverage/repair-cost";
import { useRouter } from "next/router";
import { MAX_PIXEL_HEIGHT, MAX_PIXEL_WIDTH } from "@common/constants";
import BodyStyle from "@common/enums/BodyStyle";
import LocalImage from "@common/components/LocalImage";
import { ImageWithTranslation } from "@common/types/ImageWithTranslation";
import { useTranslation } from "next-i18next";
import { useWarrantyQuotePageContext } from "@modules/purchase/WarrantyQuotePageContext";

type Props = { layout: WarrantyVehicleComponentLayout };

const getRepairCostImage = (bodyTypeId: number): ImageWithTranslation => {
  const sedan: ImageWithTranslation = {
    srcEn: SedanEn,
    srcFr: SedanFr,
    altKey: "purchase:alt_repair_cost_sedan",
  };

  const suv: ImageWithTranslation = {
    srcEn: SuvEn,
    srcFr: SuvFr,
    altKey: "purchase:alt_repair_cost_suv",
  };

  const truck: ImageWithTranslation = {
    srcEn: TruckEn,
    srcFr: TruckFr,
    altKey: "purchase:alt_repair_cost_truck",
  };

  switch (bodyTypeId) {
    case BodyStyle.Sedan:
    case BodyStyle.Coupe:
    case BodyStyle.Convertible:
    case BodyStyle.Hatchback:
      return sedan;
    case BodyStyle.SUV:
    case BodyStyle.Minivan:
    case BodyStyle.Wagon:
      return suv;
    case BodyStyle.Truck:
      return truck;
    default:
      return sedan;
  }
};

const WarrantyVehicleComponent = ({ layout }: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const { locale } = useRouter();
  const { header, background } = layout;

  const { defaultRates, rangedRates } = useWarrantyQuotePageContext();
  const warrantyRates = rangedRates?.data || defaultRates?.data;

  const repairImage = getRepairCostImage(
    layout.isDynamicVehicleImage ? warrantyRates?.asset?.bodyStyleId ?? 0 : 0
  );

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  return (
    <div
      id="cms-warranty-vehicle-repair-section"
      className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
      style={GetBackgroundStyle(background, baseDomain)}
    >
      <div className="mx-auto max-w-page px-2 pb-6 pt-10 sm:px-4 lg:px-5 lg:py-16">
        <div className="flex justify-center pb-16">
          <h3
            className="text-2xl lg:text-4xl lg:tracking-tight"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: cleanHtml(header),
            }}
          />
        </div>

        <LocalImage
          src={locale === "fr" ? repairImage.srcFr : repairImage.srcEn}
          alt={t(repairImage.altKey)}
          width={Number(MAX_PIXEL_WIDTH)}
          height={Number(MAX_PIXEL_HEIGHT)}
          color="transparent"
          className="h-auto w-full rounded-lg object-cover"
        />

        <p className="pt-6 text-center text-xs md:pt-12 md:text-base">
          {t("purchase:vehicle_repair_cost_disclaimer")}
        </p>
      </div>
    </div>
  );
};

export default WarrantyVehicleComponent;
